import { planChangeSchema, type PlanChange } from "@/schemas/billing";

/**
 * Cómo se lee la respuesta del cambio de plan. Código puro: la semántica de
 * "no hay pendiente" y "el último se rechazó" se prueba sin montar la pantalla
 * ni levantar un backend.
 */

/**
 * El pendiente, o `null` si no hay ninguno.
 *
 * **El `GET` contesta 204 cuando no hay ninguno agendado, y eso NO es un error**:
 * es el caso normal. Con 204 axios deja el cuerpo vacío, así que acá llega `""`
 * o `undefined` — parsearlo con zod tiraría y la pantalla mostraría un fallo
 * donde en realidad no pasa nada.
 */
export function interpretarPendiente(data: unknown): PlanChange | null {
  if (data === "" || data === null || data === undefined) return null;
  if (typeof data === "object" && Object.keys(data).length === 0) return null;
  return planChangeSchema.parse(data);
}

/**
 * El último cambio, sólo si fue **rechazado**.
 *
 * Es la única forma de enterarse: octopus revalida al aplicar, y para cuando
 * rechaza el pendiente ya no existe —su `GET` da 204—, así que el motivo vive
 * en el historial. Sin esto, el dueño ve que su plan no cambió y nadie le dice
 * por qué.
 *
 * Sólo cuenta el **último**: un rechazo viejo, ya resuelto por un pedido
 * posterior, no debería seguir cartelado en la pantalla.
 *
 * Se ordena acá por `requested_at` en vez de confiar en el orden que llega: el
 * backend documenta "la más nueva primero", pero de ese orden depende **cuál**
 * motivo se muestra, y eso no es algo para dar por sentado.
 */
export function ultimoRechazo(
  items: PlanChange[] | undefined
): PlanChange | null {
  if (!items?.length) return null;
  const ordenados = [...items].sort((a, b) =>
    (b.requested_at ?? "").localeCompare(a.requested_at ?? "")
  );
  const ultimo = ordenados[0];
  return ultimo.status === "rejected" ? ultimo : null;
}

/** `true` si el cambio agendado todavía se puede cancelar. */
export function sePuedeCancelar(pendiente: PlanChange | null): boolean {
  return pendiente?.status === "pending";
}

/**
 * Cómo se llama un plan de un cambio: lo que dice el servidor primero, el
 * catálogo después, y `null` si no se sabe (trd-client#264).
 *
 * # Por qué el servidor va primero
 *
 * Hasta octopus#325 el nombre se resolvía cruzando el id contra el catálogo del
 * dueño, y ese catálogo llega filtrado por `plan_is_on_offer` —activo **y**
 * público—. Un plan archivado o despublicado nunca está ahí, así que el cruce
 * fallaba justo en el caso interesante: del plan del que salís es del que más
 * suele darse de baja.
 *
 * El catálogo queda como respaldo para un octopus viejo que todavía no mande
 * los nombres, pero el orden es al revés del que había.
 *
 * # `null` no es un nombre vacío
 *
 * Devolver `null` significa **"no se sabe cómo se llama"**, y quien lo consuma
 * tiene que decir otra cosa en vez de dejar el hueco donde iba el nombre. No se
 * inventa un motivo: `null` puede venir de un plan fuera del catálogo, de una
 * contratación adoptada sin plan de origen, o de que octopus no pudo leerlo —y
 * desde acá esas tres no se distinguen—. El id sigue estando y es lo que
 * identifica.
 *
 * Un nombre en blanco del servidor tampoco es un nombre: se trata como ausente.
 */
export function nombreDePlan(
  nombre: string | null | undefined,
  id: string | null | undefined,
  planes: { plan_id: string; name?: string | null }[] = []
): string | null {
  const delServidor = nombre?.trim();
  if (delServidor) return delServidor;

  if (!id) return null;
  const delCatalogo = planes.find((p) => p.plan_id === id)?.name?.trim();
  return delCatalogo || null;
}

/**
 * El título de la tarjeta del cambio agendado.
 *
 * Se arma acá y no en el JSX porque son seis combinaciones —hay nombre de
 * destino o no, de origen o no, fecha o no— y cada una tiene que decir algo
 * distinto **sin dejar un hueco**: ese es el requisito de trd-client#264. Con
 * la fecha ya formateada, la función es texto puro y se prueba sin montar nada.
 *
 * Lo que nunca hace: nombrar un plan que no sabe cómo se llama. Si falta el
 * destino, la frase se corre a la fecha —que es el dato que el dueño necesita
 * para no creer que ya cambió— en vez de escribir "pasás a ".
 */
export function tituloDelCambioAgendado({
  destino,
  origen,
  cuando,
}: {
  destino: string | null;
  origen: string | null;
  /** La fecha del borde del ciclo, YA formateada. */
  cuando: string | null;
}): string {
  if (destino && origen) {
    return cuando
      ? `El ${cuando} pasás de ${origen} a ${destino}`
      : `Vas a pasar de ${origen} a ${destino}`;
  }
  if (destino) {
    return cuando
      ? `El ${cuando} pasás a ${destino}`
      : `Vas a pasar a ${destino}`;
  }
  return cuando
    ? `Tu plan cambia el ${cuando}`
    : "Tenés un cambio de plan agendado";
}

/**
 * El título del aviso de rechazo.
 *
 * Mismo criterio que `tituloDelCambioAgendado`: se nombra lo que se sabe y
 * nada más. Decir **cuál** cambio se rechazó importa más acá que en el
 * pendiente, porque el rechazo se lee después —el pendiente ya no existe, su
 * `GET` contesta 204— y el dueño puede no acordarse de qué había pedido.
 */
export function tituloDelRechazo({
  destino,
  origen,
}: {
  destino: string | null;
  origen: string | null;
}): string {
  if (destino && origen) {
    return `El cambio de ${origen} a ${destino} no se aplicó`;
  }
  if (destino) return `El cambio a ${destino} no se aplicó`;
  return "Tu último cambio de plan no se aplicó";
}
